import React, { useState, useEffect } from 'react';
import { catalogueAPI, Product, Category, CostRange, ProductDocument, DocumentType } from '../../services/api';
import { PlusIcon, TrashIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Card, CardBody } from '../ui';
import DocumentUpload, { PendingDocumentUpload } from '../inventory/DocumentUpload';

interface ProductModalProps {
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onCategoryCreated?: (category: Category) => void;
}

const ProductModal: React.FC<ProductModalProps> = ({ product, categories, onClose, onCategoryCreated }) => {
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    categoryId: '',
    unit: '',
    isActive: true,
    isInventoryLinked: true,
  });

  const [costRanges, setCostRanges] = useState<Partial<CostRange>[]>([]);
  const [loading, setLoading] = useState(false);

  // Inline category creation state
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [categoryList, setCategoryList] = useState<Category[]>(categories);
  const [documents, setDocuments] = useState<ProductDocument[]>([]);
  const [pendingDocuments, setPendingDocuments] = useState<PendingDocumentUpload[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    setCategoryList(categories);
  }, [categories]);

  useEffect(() => {
    if (product) {
      setFormData({
        sku: product.sku,
        name: product.name,
        description: product.description || '',
        categoryId: product.categoryId?.toString() || '',
        unit: product.unit || '',
        isActive: product.isActive,
        isInventoryLinked: product.isInventoryLinked !== false,
      });

      if (product.costRanges && product.costRanges.length > 0) {
        setCostRanges(product.costRanges);
      }
      loadDocuments(product.id);
      setPendingDocuments([]);
    } else {
      setFormData({
        sku: '',
        name: '',
        description: '',
        categoryId: '',
        unit: '',
        isActive: true,
        isInventoryLinked: true,
      });
      setCostRanges([]);
      setDocuments([]);
      setPendingDocuments([]);
    }
    setError('');
  }, [product]);

  const loadDocuments = async (productId: number) => {
    try {
      const docs = await catalogueAPI.getDocuments(productId);
      setDocuments(docs);
    } catch (loadError) {
      console.error('Error loading product documents:', loadError);
    }
  };

  const handleUploadDocument = async (
    file: File,
    fileType: DocumentType,
    fileName: string,
    parentDocumentId?: number,
    parentClientId?: string,
  ) => {
    if (product) {
      try {
        await catalogueAPI.uploadDocument(product.id, file, fileType, fileName, parentDocumentId);
        await loadDocuments(product.id);
      } catch (uploadError: any) {
        setError(uploadError.response?.data?.message || 'Failed to upload document');
        throw uploadError;
      }
    } else {
      setPendingDocuments((current) => [
        ...current,
        {
          clientId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file,
          fileType,
          fileName,
          parentDocumentId,
          parentClientId,
        },
      ]);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      await catalogueAPI.deleteDocument(documentId);
      if (product) {
        await loadDocuments(product.id);
      }
    } catch (deleteError: any) {
      setError(deleteError.response?.data?.message || 'Failed to delete document');
      throw deleteError;
    }
  };

  const handleDeletePendingDocument = (clientId: string) => {
    const blockedIds = new Set<string>([clientId]);
    let changed = true;

    while (changed) {
      changed = false;
      pendingDocuments.forEach((document) => {
        if (document.parentClientId && blockedIds.has(document.parentClientId) && !blockedIds.has(document.clientId)) {
          blockedIds.add(document.clientId);
          changed = true;
        }
      });
    }

    setPendingDocuments((current) => current.filter((document) => !blockedIds.has(document.clientId)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data: any = {
        ...formData,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : undefined,
        // Only include costRanges if there are any; allows saving with zero costs
        costRanges: costRanges.length > 0
          ? costRanges.map((range) => ({
              minQuantity: range.minQuantity || 0,
              maxQuantity: range.maxQuantity || undefined,
              unit: range.unit || formData.unit || undefined,
              pricePerUnit: parseFloat(range.pricePerUnit?.toString() || '0'),
              currency: range.currency || 'USD',
            }))
          : [],
      };

      let savedProductId = product?.id;

      if (product) {
        await catalogueAPI.updateProduct(product.id, data);
      } else {
        const createdProduct = await catalogueAPI.createProduct(data);
        savedProductId = createdProduct.id;

        if (savedProductId && pendingDocuments.length > 0) {
          const pendingMap = new Map<string, number>();
          const remainingDocuments = [...pendingDocuments];

          while (remainingDocuments.length > 0) {
            let progressed = false;

            for (let index = 0; index < remainingDocuments.length; index += 1) {
              const doc = remainingDocuments[index];
              if (doc.parentClientId && !pendingMap.has(doc.parentClientId)) {
                continue;
              }

              try {
                const createdDocument = await catalogueAPI.uploadDocument(
                  savedProductId,
                  doc.file,
                  doc.fileType,
                  doc.fileName,
                  doc.parentClientId ? pendingMap.get(doc.parentClientId) : doc.parentDocumentId,
                );
                pendingMap.set(doc.clientId, createdDocument.id);
              } catch (uploadError) {
                console.error('Failed to upload document:', uploadError);
              }

              remainingDocuments.splice(index, 1);
              index -= 1;
              progressed = true;
            }

            if (!progressed) {
              console.error('Some pending child documents could not be linked because their parent was missing.');
              break;
            }
          }
        }
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving product:', error);
      setError(error.response?.data?.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const addCostRange = () => {
    setCostRanges([
      ...costRanges,
      { minQuantity: 0, maxQuantity: undefined, unit: formData.unit || undefined, pricePerUnit: '0', currency: 'USD' },
    ]);
  };

  const removeCostRange = (index: number) => {
    setCostRanges(costRanges.filter((_, i) => i !== index));
  };

  const updateCostRange = (index: number, field: string, value: any) => {
    const updated = [...costRanges];
    updated[index] = { ...updated[index], [field]: value };
    setCostRanges(updated);
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    try {
      const created = await catalogueAPI.createCategory({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
      });
      const newCat = created as Category;
      setCategoryList((prev) => [...prev, newCat]);
      setFormData((prev) => ({ ...prev, categoryId: newCat.id.toString() }));
      onCategoryCreated?.(newCat);
      setShowNewCategory(false);
      setNewCategoryName('');
      setNewCategoryDescription('');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create category');
    } finally {
      setCreatingCategory(false);
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={product ? 'Edit Product' : 'Add Product'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-neutral-700">
              SKU <span className="text-error-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">
              Product Name <span className="text-error-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            />
          </div>

          {/* Category with inline creation */}
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-neutral-700 mb-1">Category</label>
            {!showNewCategory ? (
              <div className="flex items-center gap-2">
                <select
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                  className="flex-1 border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
                >
                  <option value="">No Category</option>
                  {categoryList.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowNewCategory(true)}
                  leftIcon={<PlusIcon className="h-4 w-4" />}
                >
                  New Category
                </Button>
              </div>
            ) : (
              <div className="border border-primary-300 rounded-lg bg-primary-50 p-3 space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-primary-700">Create New Category</span>
                  <button
                    type="button"
                    onClick={() => { setShowNewCategory(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Category name *"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="block w-full border border-neutral-300 rounded-md shadow-sm py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                  autoFocus
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newCategoryDescription}
                  onChange={(e) => setNewCategoryDescription(e.target.value)}
                  className="block w-full border border-neutral-300 rounded-md shadow-sm py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                />
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleCreateCategory}
                    isLoading={creatingCategory}
                    disabled={!newCategoryName.trim()}
                    leftIcon={<CheckIcon className="h-4 w-4" />}
                  >
                    Create
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowNewCategory(false); setNewCategoryName(''); setNewCategoryDescription(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700">Unit</label>
            <input
              type="text"
              placeholder="e.g., kg, liter, piece"
              value={formData.unit}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-700">Description</label>
          <textarea
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="mt-1 block w-full border border-neutral-300 rounded-lg shadow-sm py-2 px-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-colors"
          />
        </div>

        {/* Cost Ranges — optional */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <label className="block text-sm font-medium text-neutral-700">Cost Ranges</label>
              <p className="text-xs text-neutral-500 mt-0.5">Optional — can be added later</p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={addCostRange}
              leftIcon={<PlusIcon className="h-4 w-4" />}
            >
              Add Range
            </Button>
          </div>

          {costRanges.length === 0 ? (
            <div className="text-center py-4 border-2 border-dashed border-neutral-200 rounded-lg text-neutral-400 text-sm">
              No cost ranges yet — click <strong>Add Range</strong> to add pricing
            </div>
          ) : (
            <div className="space-y-3">
              {costRanges.map((range, index) => (
                <Card key={index} variant="bordered">
                  <CardBody className="py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2 lg:grid-cols-4">
                        <div>
                          <label className="block text-xs text-neutral-600 mb-1">Min Qty</label>
                          <input
                            type="number"
                            min="0"
                            value={range.minQuantity || 0}
                            onChange={(e) =>
                              updateCostRange(index, 'minQuantity', parseInt(e.target.value))
                            }
                            className="block w-full border border-neutral-300 rounded-lg shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-neutral-600 mb-1">
                            Max Qty (optional)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={range.maxQuantity || ''}
                            onChange={(e) =>
                              updateCostRange(
                                index,
                                'maxQuantity',
                                e.target.value ? parseInt(e.target.value) : undefined
                              )
                            }
                            placeholder="Unlimited"
                            className="block w-full border border-neutral-300 rounded-lg shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-neutral-600 mb-1">Unit</label>
                          <input
                            type="text"
                            value={range.unit || ''}
                            onChange={(e) => updateCostRange(index, 'unit', e.target.value)}
                            placeholder={formData.unit || 'unit'}
                            className="block w-full border border-neutral-300 rounded-lg shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-neutral-600 mb-1">Price/Unit</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={range.pricePerUnit || 0}
                            onChange={(e) => updateCostRange(index, 'pricePerUnit', e.target.value)}
                            className="block w-full border border-neutral-300 rounded-lg shadow-sm py-1 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeCostRange(index)}
                        className="text-error-600 hover:text-error-900 transition-colors"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
          />
          <label className="ml-2 block text-sm text-neutral-900">Active</label>
        </div>

        <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
          <label className="flex items-start gap-3 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={formData.isInventoryLinked}
              onChange={(e) => setFormData({ ...formData, isInventoryLinked: e.target.checked })}
              className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span>
              <span className="block font-medium text-neutral-900">Link to inventory</span>
              <span className="mt-1 block text-neutral-500">
                Linked products show out-of-stock warnings when quoted.
              </span>
            </span>
          </label>
        </div>

        <div className="border-t pt-4">
          <h4 className="mb-3 text-sm font-medium text-neutral-900">Documents</h4>
          <DocumentUpload
            documents={documents}
            pendingDocuments={pendingDocuments}
            onUpload={handleUploadDocument}
            onDelete={handleDeleteDocument}
            onDeletePending={handleDeletePendingDocument}
            disabled={loading}
            supportsChildDocuments
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={loading}>
            {product ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ProductModal;
